import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type UUID } from '~/types/common';

import { useMutationDeleteTaxCode } from '~/data/taxCode';

import { truncateDecimals } from '@vestigas/utils';
import { i18n } from '~/utils';

import {
  Alert,
  AlertTitle,
  ErrorState,
  Fieldset,
  FormRow,
  Icon,
  LoadingOverlay,
  Reveal,
} from '@vestigas/ui';
import { ConfirmDialog, HttpValidationErrorAlert } from '~/ui/molecules';

import { useTaxCodeForm } from '../useTaxCodeForm';

const fieldLabels = {
  applyToAllCompaniesInAccount: i18n.t(
    ($) => $.form.fields.applyToAllCompaniesInAccount.label,
    { ns: 'taxCodes' },
  ),
  code: i18n.t(($) => $.form.fields.code.label, { ns: 'taxCodes' }),
  companyIds: i18n.t(($) => $.form.fields.companyIds.label, {
    ns: 'taxCodes',
  }),
  isReverseCharge: i18n.t(($) => $.form.fields.isReverseCharge.label, {
    ns: 'taxCodes',
  }),
  name: i18n.t(($) => $.form.fields.name.label, { ns: 'taxCodes' }),
  rate: i18n.t(($) => $.form.fields.rate.label, { ns: 'taxCodes' }),
};

const RATE_DIGITS = 2;

type P = {
  readonly onCancel: () => void;
  readonly onSuccess: () => void;
  readonly taxCodeId?: UUID;
} & ComponentStyling;

export function TaxCodeForm({
  className,
  onCancel,
  onSuccess,
  style,
  taxCodeId,
}: P) {
  const { t } = useTranslation('taxCodes');

  const companyFieldContainerRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { form, isEditMode, isLoading, isPending, mutationError, queryError } =
    useTaxCodeForm({
      onSuccess,
      taxCodeId,
    });

  const deleteMutation = useMutationDeleteTaxCode({
    onSuccess() {
      setIsDeleteConfirmOpen(false);
      onSuccess();
    },
  });

  const status = mutationError?.response?.status;
  const validationError = status === 422 ? mutationError : null;
  const hasRequestError = Boolean(mutationError) && status !== 422;

  useEffect(() => {
    if (isLoading || queryError) return;

    const animationFrameId = requestAnimationFrame(() => {
      if (isEditMode) {
        nameInputRef.current?.focus();
      } else {
        companyFieldContainerRef.current
          ?.querySelector<HTMLElement>('[role="combobox"]')
          ?.focus();
      }
    });

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isEditMode, isLoading, queryError]);

  if (queryError) {
    return (
      <div className="flex min-h-80 items-center justify-center">
        <ErrorState
          className={className}
          errorMessage={t(($) => $.form.errors.loadFailed)}
          style={style}
        />
      </div>
    );
  }

  return (
    <form
      id="tax-code-form"
      className={className}
      style={style}
      onSubmit={(event) => {
        event.preventDefault();

        void form.handleSubmit();
      }}
    >
      {hasRequestError ? (
        <Alert className="mb-4" severity="error">
          <AlertTitle>
            {isEditMode
              ? t(($) => $.form.errors.updateRequest.title)
              : t(($) => $.form.errors.request.title)}
          </AlertTitle>
          {isEditMode
            ? t(($) => $.form.errors.updateRequest.description)
            : t(($) => $.form.errors.request.description)}
        </Alert>
      ) : null}
      <HttpValidationErrorAlert<'taxCodes', 'form.fields'>
        className="mb-4"
        error={validationError}
        isSubmitting={isPending}
        fieldTransKey={(field) =>
          Object.hasOwn(fieldLabels, field)
            ? fieldLabels[field as keyof typeof fieldLabels]
            : 'untranslated'
        }
      />

      <Fieldset bordered={false} padded={false}>
        <LoadingOverlay isLoading={isLoading} className="-inset-2" />

        <FormRow vAlign="top">
          {isEditMode ? (
            <form.AppField name="companyName">
              {(field) => (
                <field.TextField
                  isDisabled
                  label={t(($) => $.form.fields.companyIds.label)}
                />
              )}
            </form.AppField>
          ) : (
            <form.Subscribe
              selector={(state) => state.values.applyToAllCompaniesInAccount}
            >
              {(applyToAllCompanies) => (
                <div className="flex-1" ref={companyFieldContainerRef}>
                  <form.AppField name="companyIds">
                    {(field) => (
                      <field.MultiSelectCompaniesField
                        isDisabled={applyToAllCompanies}
                        label={t(($) => $.form.fields.companyIds.label)}
                        placeholder={t(
                          ($) => $.form.fields.companyIds.placeholder,
                        )}
                      />
                    )}
                  </form.AppField>
                </div>
              )}
            </form.Subscribe>
          )}

          {!isEditMode ? (
            <form.AppField
              name="applyToAllCompaniesInAccount"
              listeners={{
                onChange: ({ fieldApi, value: applyToAllCompanies }) => {
                  if (applyToAllCompanies) {
                    fieldApi.form.setFieldValue('companyIds', []);
                  }
                },
              }}
            >
              {(field) => (
                <field.SwitchField
                  className="mt-5 w-fit shrink-0"
                  label={t(
                    ($) => $.form.fields.applyToAllCompaniesInAccount.label,
                  )}
                />
              )}
            </form.AppField>
          ) : null}
        </FormRow>

        <FormRow vAlign="top">
          <form.AppField name="name">
            {(field) => (
              <field.TextField
                className="min-w-80"
                label={t(($) => $.form.fields.name.label)}
                placeholder={t(($) => $.form.fields.name.placeholder)}
                ref={nameInputRef}
              />
            )}
          </form.AppField>
          <form.AppField
            name="rate"
            listeners={{
              onChange: ({ fieldApi, value: rate }) => {
                if (typeof rate !== 'number') {
                  return;
                }

                const truncatedRate = truncateDecimals(rate, RATE_DIGITS);
                if (truncatedRate !== rate) {
                  fieldApi.setValue(truncatedRate, { dontRunListeners: true });
                }
              },
            }}
          >
            {(field) => (
              <field.LocalizedNumberField
                className="w-36 flex-none"
                label={t(($) => $.form.fields.rate.label)}
                maxDigits={RATE_DIGITS}
                placeholder={t(($) => $.form.fields.rate.placeholder)}
                unit="%"
              />
            )}
          </form.AppField>
        </FormRow>

        <form.Subscribe
          selector={(state) =>
            state.values.isReverseCharge && state.values.rate > 0
          }
        >
          {(showReverseChargeHint) => (
            <Reveal show={showReverseChargeHint}>
              <Alert severity="info">
                {t(($) => $.form.fields.rate.reverseChargeHint)}
              </Alert>
            </Reveal>
          )}
        </form.Subscribe>

        <FormRow vAlign="top">
          <form.AppField name="code">
            {(field) => (
              <field.TextField
                className="min-w-80"
                label={t(($) => $.form.fields.code.label)}
                placeholder={t(($) => $.form.fields.code.placeholder)}
              />
            )}
          </form.AppField>
          <form.AppField name="isReverseCharge">
            {(field) => (
              <field.CheckboxField
                className="mt-5 flex-1"
                label={t(($) => $.form.fields.isReverseCharge.label)}
              />
            )}
          </form.AppField>
        </FormRow>
      </Fieldset>

      <form.AppForm>
        <form.ActionsRow
          className="mt-8 p-0"
          hasResetButton={false}
          requireDirty={isEditMode}
          tertiaryAction={
            isEditMode
              ? {
                  disabled: deleteMutation.isPending,
                  icon: <Icon metaphor="delete" />,
                  onClick: () => {
                    deleteMutation.reset();
                    setIsDeleteConfirmOpen(true);
                  },
                  text: t(($) => $.form.deleteButton.text),
                }
              : undefined
          }
          onCancel={onCancel}
        />
      </form.AppForm>
      <ConfirmDialog
        confirmText={t(($) => $.form.deleteConfirm.confirmButton)}
        content={
          <>
            {t(($) => $.form.deleteConfirm.content)}
            {deleteMutation.isError ? (
              <span className="text-tiga-error mt-4 block" role="alert">
                {t(($) => $.form.errors.deleteRequest)}
              </span>
            ) : null}
          </>
        }
        isConfirming={deleteMutation.isPending}
        isOpen={isDeleteConfirmOpen}
        testId="delete-tax-code-confirm-dialog"
        title={t(($) => $.form.deleteConfirm.title)}
        onCancel={() => setIsDeleteConfirmOpen(false)}
        onConfirm={() => {
          if (taxCodeId) {
            deleteMutation.mutate(taxCodeId);
          }
        }}
      />
    </form>
  );
}
